
import { useState, useEffect, useCallback } from 'react';
import { TrackDownload } from '@/lib/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { deleteTrack } from './track-upload/utils/db/deleteTrack';

export const useArtistData = () => {
  const [uploads, setUploads] = useState<TrackDownload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [artistId, setArtistId] = useState<string | null>(null);

  const fetchTracks = useCallback(async (id: string) => {
    console.log("Fetching tracks for artist ID:", id);
    const { data: tracksData, error: tracksError } = await supabase
      .from('tracks')
      .select('*')
      .eq('artist_id', id)
      .order('created_at', { ascending: false });
    
    if (tracksError) {
      console.error('Error fetching tracks:', tracksError);
      toast.error("Failed to load your tracks");
      return false;
    }
    
    if (tracksData) {
      console.log(`Found ${tracksData.length} tracks for artist ID: ${id}`);
      const formattedTracks: TrackDownload[] = tracksData.map(track => ({
        id: track.id,
        trackId: track.track_id,
        spotifyUrl: track.spotify_url,
        artistId: track.artist_id,
        fileUrl: track.file_url,
        fileName: track.file_name,
        fileType: track.file_type,
        fileSize: track.file_size,
        downloadCount: track.download_count,
        createdAt: track.created_at,
        trackName: track.track_title || undefined,
        artistName: track.artist_name,
        description: track.description
      }));
      
      setUploads(formattedTracks);
      return true;
    }
    return false;
  }, []);

  const fetchArtistData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error("No authenticated user found");
        toast.error("Please sign in to view your dashboard");
        return false;
      }
      
      console.log("Fetching artist data for user ID:", user.id);
      
      const { data: artistData, error: artistError } = await supabase
        .from('artists')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (artistError || !artistData) {
        console.log('No artist record found, creating one...');
        
        const { data: newArtist, error: createError } = await supabase
          .from('artists')
          .insert({
            user_id: user.id,
            name: user.email?.split('@')[0] || 'Artist',
            created_at: new Date().toISOString()
          })
          .select('id')
          .single();
        
        if (createError || !newArtist) {
          console.error('Error creating artist:', createError);
          toast.error("There was a problem setting up your artist profile");
          return false;
        }
        
        console.log("Created new artist with ID:", newArtist.id);
        setArtistId(newArtist.id);
        
        return await fetchTracks(newArtist.id);
      } else {
        console.log("Found existing artist with ID:", artistData.id);
        setArtistId(artistData.id);
        
        return await fetchTracks(artistData.id);
      }
    } catch (error) {
      console.error('Error in dashboard:', error);
      toast.error("There was a problem loading your tracks");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchTracks]);
  
  useEffect(() => {
    fetchArtistData();
  }, [fetchArtistData]);

  const refreshData = async () => {
    if (!artistId) return false;
    
    try {
      return await fetchTracks(artistId);
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error("There was a problem refreshing your tracks");
      return false;
    }
  };

  const addUpload = (newUpload: TrackDownload) => {
    setUploads(prevUploads => [newUpload, ...prevUploads]);
  };

  const removeUpload = async (trackId: string): Promise<boolean> => {
    try {
      console.log("Starting track deletion process for track ID:", trackId);
      
      const { success, error } = await deleteTrack(trackId);
      
      if (success) {
        console.log("Track deleted successfully, updating UI state");
        // Update the state after successful deletion from the database
        setUploads(prevUploads => prevUploads.filter(upload => upload.id !== trackId));
        
        toast.success("Track has been successfully deleted");
        return true;
      } else {
        console.error("Error returned from deleteTrack:", error);
        throw error;
      }
    } catch (error) {
      console.error("Error deleting track:", error);
      toast.error("There was a problem deleting your track. Please try again.");
      return false;
    }
  };

  return {
    uploads,
    isLoading,
    artistId,
    addUpload,
    removeUpload,
    refreshData
  };
};
